import { Col, Row, Spin, Modal } from "antd";
import { MainContainer } from "Pages/TestEvaluation/TestEvaluation";
import React, { useContext, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { UserInfoContext } from "Context/CommonContext";
import { pushToDataLayerForGTM } from "utilFunctions/helperFunctions/analytics";
import {
  ArrowLeftOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import StudentQuestionComponent from "./Components/StudentQuestionComponent";
import PdfAnnotator from "./Components/PdfAnnotator";
import {
  useEvaluateStudentTest,
  useSubjectiveTestDetail,
  useGenerateSubjectivePresignedUrl,
  useDeleteEvaluationLock,
} from "../../DRS/Context/MyBatchContext";
import EvaluationFooter from "Pages/TestEvaluation/StudentEvaluation/Components/EvaluationFooter";
import { uploadFileToS3 } from "utilFunctions/helperFunctions/utilFunctions";
import {
  BASE_URL,
  SUBJECTIVE_TEST,
} from "utilFunctions/Constants/urlConstants";
import styled from "styled-components";
import { StyledTitle } from "Pages/TestEvaluation/StudentEvaluation/Components/ChallengedZoneTab";
import { getPopUpMessage } from "Pages/TestEvaluation/utils/studentEvalUtil";

const getFirstPageNumber = (answerPagesNo?: string | null): number | null => {
  if (!answerPagesNo) return null;
  const cleaned = answerPagesNo.trim();
  if (!cleaned) return null;

  const parts = cleaned.split(",");
  if (parts.length > 0) {
    const firstPart = parts[0].trim();
    const pageNum = parseInt(firstPart, 10);
    if (!isNaN(pageNum)) {
      return pageNum;
    }
  }
  return null;
};

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  gap: 16px;

  .loading-text {
    font-size: 18px;
    font-weight: 600;
    color: #1890ff;
    letter-spacing: 0.5px;
  }
`;

const StudentEvaluation = () => {
  const annotatorRef = useRef<any>(null);
  const studentQuestionRef = useRef<any>(null);
  const startTimeRef = useRef<number>(Date.now());
  const userInfo: any = useContext(UserInfoContext);
  const navigate = useNavigate();
  const { screen } = useWindowSize();
  const { studentId } = useParams();
  const { state } = useLocation();
  const { record, test, totalMarks, routeAction } = state || {};
  const { fetchSubjectiveTestDetail, testDetail } = useSubjectiveTestDetail();
  const { code, message } = testDetail || {};
  const [studentChallengedMarks, setStudentChallengedMarks] = useState<
    Record<number, any>
  >({});
  const { data } = testDetail || {};
  const { questions = [] } = data || {};
  const questionsMapping = useMemo(() => {
    const mapping: Record<number, any> = {};
    questions.forEach((question: any) => {
      mapping[question.id] = question;
    });
    return mapping;
  }, [questions]);
  const action = record?.action || "evaluate"; // default to "evaluate" if action is missing
  const sectionSubmissionId =
    record?.submissionId || testDetail?.data?.submissionId;
  const routeBackPreviousPage = () => {
    navigate("/admin/test-evaluation/" + test?.id, {
      state: {
        ...state,
        test: test,
        batchId: state?.batchId,
        batchDetail: state?.batchDetail,
        tabIndex: "3",
        routeAction: routeAction,
      },
    });
  };
  React.useEffect(() => {
    if (code === "EUP" && message) {
      Modal.warning({
        title: "Evaluation Under Progress",
        content: message,
        okText: "Continue",
        keyboard: false,
        onOk: () => {
          routeBackPreviousPage();
        },
      });
    }
  }, [code, message]);

  React.useEffect(() => {
    if (sectionSubmissionId) {
      const url =
        action === "view"
          ? `${BASE_URL}${SUBJECTIVE_TEST}/${sectionSubmissionId}`
          : `${BASE_URL}${SUBJECTIVE_TEST}/evaluate/${sectionSubmissionId}`;
      fetchSubjectiveTestDetail(url, sectionSubmissionId);
    }
  }, [sectionSubmissionId, action]);

  // Extract PDF URL from testDetail - assuming it's in a standard field
  const pdfUrl =
    testDetail?.data?.answerSheet || testDetail?.data?.answerSheetUrl;

  const isMobile = ["sm", "md"].includes(screen);

  const desktopScreen = {
    sm: 24,
    xs: 24,
    md: 24,
    lg: 16,
    xl: 16,
    xxl: 16,
  };
  const mobileScreen = {
    sm: 24,
    xs: 24,
    md: 24,
    lg: 8,
    xl: 8,
    xxl: 8,
  };

  const [isProcessing, setIsProcessing] = React.useState(false);

  const { evaluateStudentTest, loading: saving } = useEvaluateStudentTest(
    () => {
      setIsProcessing(false);
      const evaluationData = studentQuestionRef.current?.getEvaluationData();
      const totalScore = (evaluationData?.data?.questions || []).reduce(
        (acc: number, q: any) =>
          acc +
          (q.isAttempted === false
            ? 0
            : studentChallengedMarks[q.id] != null
            ? studentChallengedMarks[q.id]
            : q.obtainedMarks || 0),
        0
      );
      pushToDataLayerForGTM({
        event: "ag_test_compliance_paper_evaluation_submitted",
        TEST_ID: test?.id,
        STATUS_TAB: (state as any)?.statusTab,
        TEST_TYPE: test?.format,
        MEDIUM: test?.mode,
        TAB_NAME: "Evaluate",
        ACTION_SELECTED: record?.action,
        PSID: record?.psid,
        USER_ROLE: userInfo?.fexRole,
        EMPLOYEE_ID: userInfo?.employeeId,
        TOTAL_SCORE: totalScore,
        TIME_SPENT_SEC: Math.floor((Date.now() - startTimeRef.current) / 1000),
      });
      const { challengedQuestionsArray } = getChallengedQuestions();
      if (challengedQuestionsArray.size) {
        evaluatedCompleted(challengedQuestionsArray);
      } else {
        routeBackPreviousPage();
      }
    },
    () => {
      setIsProcessing(false);
    }
  );
  const evaluatedCompleted = (challengedQuestionsArray: any) => {
    const remainingChallengedQuestions = Array.from(
      challengedQuestionsArray
    ).filter((id: any) => !studentChallengedMarks[id]);
    const status =
      remainingChallengedQuestions.length == 0 ? "COMPLETED" : "IN_PROGRESS";
    const { PRIMARY_MESSAGE, SECONDARY_MESSAGE, THIRD_MESSAGE } =
      getPopUpMessage(status);
    const secondMessage =
      status === "COMPLETED"
        ? SECONDARY_MESSAGE
        : `${SECONDARY_MESSAGE} ${
            challengedQuestionsArray.size - remainingChallengedQuestions.length
          } challenged question.${
            remainingChallengedQuestions.length
          } still pending- ${THIRD_MESSAGE}`;
    Modal.warning({
      title: <div>{PRIMARY_MESSAGE}</div>,
      content: <div>{secondMessage}</div>,
      okText: "Continue",
      keyboard: false,
      onOk: () => {
        routeBackPreviousPage();
      },
    });
  };
  const { generatePresignedUrl, loading: generatingUrl } =
    useGenerateSubjectivePresignedUrl(
      async (response) => {
        try {
          const { data } = response || {};
          const { url, preSignedUrl } = data || {};
          let pdfBuffer = null;

          if (annotatorRef.current) {
            pdfBuffer = await annotatorRef.current.getAnnotatedPdf();
          }
          if (preSignedUrl && pdfBuffer) {
            await uploadFileToS3(preSignedUrl, pdfBuffer);
            await handleSave(url);
          } else {
            setIsProcessing(false);
          }
        } catch (error) {
          setIsProcessing(false);
        }
      },
      () => {
        setIsProcessing(false);
      }
    );
  const showValidations = () => {
    let evaluationData = null;

    if (studentQuestionRef.current) {
      evaluationData = studentQuestionRef.current.getEvaluationData();
    }
    const evaluated_questions = evaluationData?.data?.questions || [];
    const challengedQuestions = evaluated_questions
      .filter(
        (question: any) =>
          studentChallengedMarks[question.id] &&
          question?.status === "CHALLENGED" &&
          question?.evaluatedComment == ""
      )
      .map((ques: any) => ques.id);
    const quesStr = questionStringBuilder(challengedQuestions);
    if (!challengedQuestions.length) return false;
    Modal.warning({
      title: (
        <div>
          Please fix the errors before saving. Comment is mandatory for
          re-evaluated questions:-
          <StyledTitle
            style={{
              fontSize: "16px",
            }}
          >
            {" " + quesStr}
          </StyledTitle>
        </div>
      ),
      content: "",
      okText: "Continue",
      keyboard: false,
      onOk: () => {
        return true;
      },
    });
    return true;
  };
  const getChallengedQuestions = () => {
    let evaluationData = null;

    if (studentQuestionRef.current) {
      evaluationData = studentQuestionRef.current.getEvaluationData();
    }
    const challengedQuestionsArray = new Set(
      evaluationData?.data?.questions
        ?.filter((question: any) => question.status === "CHALLENGED")
        .map((ques: any) => ques.id) ?? []
    );
    return {
      challengedQuestionsArray,
      evaluationData,
    };
  };
  const handleSave = async (url: string) => {
    const { challengedQuestionsArray, evaluationData } =
      getChallengedQuestions();
    const testScheduleId = test?.id;
    const evaluated_questions = evaluationData?.data?.questions
      .filter(
        (question: any) =>
          !(
            challengedQuestionsArray.has(question.id) &&
            question.evaluatedComment === ""
          )
      )
      .map((q: any) => {
        if (q.isAttempted === false) {
          return {
            questionSubmissionId: q.id,
            obtainedMarks: 0,
            evaluatedComment: q.evaluatedComment || "",
            isAttempted: false,
          };
        }
        return {
          questionSubmissionId: q.id,
          obtainedMarks: q.obtainedMarks,
          evaluatedComment: q.evaluatedComment || "",
          isAttempted: q.isAttempted !== false,
        };
      });
    const payload = {
      evaluatedSheetUrl: url,
      batchId: state?.batchId,
      testScheduleId: testScheduleId,
      submissionId: sectionSubmissionId,
      sections: evaluationData?.data?.sections
        ?.map((s: any) => ({
          sectionSubmissionId: s.id,
          sectionComment: s.sectionComment || "",
        }))
        .filter(
          (s: any) => s.sectionComment !== "" && s.sectionComment !== null
        ),
      questions: evaluated_questions
        .filter(
          (ele: any) =>
            (ele.obtainedMarks !== undefined && ele.obtainedMarks !== null) ||
            ele.evaluatedComment != ""
        )
        .map((ele: any) => ({
          ...ele,
          obtainedMarks: studentChallengedMarks[ele.questionSubmissionId]
            ? studentChallengedMarks[ele.questionSubmissionId]
            : ele.obtainedMarks,
        })),
    };
    evaluateStudentTest(payload);
  };

  const questionStringBuilder = (ques: number[] = []) => {
    return ques
      .map((id) => `Q.${questionsMapping[id]?.questionNumber ?? id}`)
      .join(", ");
  };

  const handleAutoSave = React.useCallback(() => {
    if (showValidations()) {
      return;
    }

    setIsProcessing(true);
    generatePresignedUrl({
      testScheduleId: Number(test?.id),
      submissionId: Number(sectionSubmissionId),
      batchId: state?.batchId,
      fileName: `evaluation_${studentId}_${sectionSubmissionId}.pdf`,
    });
  }, [
    test?.id,
    sectionSubmissionId,
    state?.batchId,
    studentId,
    generatePresignedUrl,
  ]);

  const { deleteEvaluationLock, loading: discarding } = useDeleteEvaluationLock(
    () => {
      routeBackPreviousPage();
    },
    () => {
      console.error("Failed to delete evaluation lock");
    }
  );

  const isLoading = saving || generatingUrl || isProcessing || discarding;

  const onDiscard = () => {
    Modal.confirm({
      title: "Discard Evaluation?",
      icon: <ExclamationCircleOutlined />,
      content:
        "Are you sure you want to discard the evaluation? All unsaved changes will be lost and the lock will be released.",
      okText: "Yes, Discard",
      okType: "danger",
      cancelText: "No, Continue",
      onOk() {
        if (sectionSubmissionId) {
          deleteEvaluationLock(sectionSubmissionId);
        }
      },
    });
  };

  const handleQuestionClick = (q: any) => {
    if (!q || !q.answerPagesNo) return;
    const pageNum = getFirstPageNumber(q.answerPagesNo);
    if (pageNum !== null && annotatorRef.current) {
      annotatorRef.current.jumpToPage(pageNum);
    }
  };

  const isSaveDisabled = useMemo(() => {
    const challengedQuestions =
      questions
        ?.filter((question: any) => question.status === "CHALLENGED")
        .map((ques: any) => ques.id) ?? [];
    if (!challengedQuestions.length) return false;
    return !Array.from(challengedQuestions).some(
      (id: any) =>
        studentChallengedMarks[id] !== undefined &&
        studentChallengedMarks[id] !== null
    );
  }, [studentQuestionRef, studentChallengedMarks, questions]);
  return (
    <MainContainer
      screen={screen}
      style={{ paddingBottom: isMobile ? "120px" : "0" }}
    >
      {isLoading && (
        <LoadingOverlay>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <span className="loading-text">Saving Evaluation...</span>
          <span
            style={{ color: "#8c8c8c", fontSize: "12px", marginTop: "-8px" }}
          >
            Please do not close the window
          </span>
        </LoadingOverlay>
      )}
      <Row gutter={isMobile ? [0, 0] : [20, 20]}>
        <Col {...desktopScreen}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? "8px" : "16px",
              marginBottom: isMobile ? "12px" : "24px",
              padding: isMobile ? "12px" : 0,
              background: isMobile ? "#fff" : "transparent",
              borderBottom: isMobile ? "1px solid #f0f0f0" : "none",
            }}
          >
            <div
              onClick={() => {
                if (action == "view") {
                  routeBackPreviousPage();
                } else {
                  onDiscard();
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <ArrowLeftOutlined
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  color: "#1890ff",
                }}
              />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: isMobile ? "16px" : "20px",
                  fontWeight: 800,
                  color: "#262626",
                  letterSpacing: "-0.5px",
                }}
              >
                Evaluation: {record?.name || "Student"}
              </h2>
              <p
                style={{
                  color: "#8c8c8c",
                  margin: 0,
                  fontSize: isMobile ? "11px" : "13px",
                  fontWeight: 500,
                }}
              >
                {test?.testName || "Subjective Test"} • {record?.psid}
              </p>
            </div>
          </div>
          <PdfAnnotator key={studentId} ref={annotatorRef} pdfUrl={pdfUrl} />
        </Col>
        <Col {...mobileScreen}>
          <StudentQuestionComponent
            ref={studentQuestionRef}
            testScheduleId={test?.id}
            sectionSubmissionId={sectionSubmissionId}
            initialData={testDetail}
            onTimeUp={handleAutoSave}
            totalMarks={totalMarks}
            studentTestStatus={record?.rawStatus}
            onQuestionClick={handleQuestionClick}
            action={action}
            studentChallengedMarks={studentChallengedMarks}
            setStudentChallengedMarks={setStudentChallengedMarks}
          />
        </Col>
      </Row>
      {action != "view" && (
        <EvaluationFooter
          onSave={handleAutoSave}
          loading={isLoading}
          onDiscard={onDiscard}
          isSaveDisabled={isSaveDisabled}
        />
      )}
    </MainContainer>
  );
};

export default StudentEvaluation;
